import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'react-bootstrap';
import { convertDoubleToStringPTBR } from '../../../../../utils/ConverterUtils';
import './PlanDetails.css';

/**
 * Resumo do plano escolhido, com um detalhamento que abre sob demanda.
 *
 * A recepção vende o plano na frente do cliente e precisa responder "o que eu
 * ganho com isso?" sem sair do cadastro. O que sempre importa (nome, valor,
 * periodicidade) fica visível; o resto abre no clique para não empurrar os
 * campos de pagamento para baixo.
 *
 * @param {object|null} plan opção selecionada no PlanSelect, já com os campos do plano
 */
const PlanDetails = ({ plan, showDetailsButton = true, planStatus = null }) => {

    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const price = plan?.price ?? plan?.creditCardPrice;
    const products = plan?.planProducts || [];

    // O enum de periodicidade chega em maiúsculas (MONTHLY); as traduções usam a
    // chave em minúsculas. Sem tradução, o i18next devolve a própria chave.
    // Pacote em dias mostra a duração no lugar da periodicidade, que não vale para ele.
    const packageDays = Number(plan?.packageDays) || 0;
    const periodicityLabel = packageDays > 0
        ? `${packageDays} ${packageDays === 1 ? 'dia' : 'dias'}`
        : (plan?.periodicity ? t(String(plan.periodicity).toLowerCase()) : '-');

    // Zero e nulo significam a mesma coisa nesses dois campos: sem limite.
    const maxAppointmentsLabel = plan?.maxAppointments ? plan.maxAppointments : t('unlimited');
    const qtyTransactionsLabel = plan?.qtyTransactions ? plan.qtyTransactions : t('indeterminate');

    // Sem plano escolhido não há o que detalhar, mas o resumo continua na tela
    // como moldura dos campos que vêm abaixo.
    const hasPlan = Boolean(plan);

    return (
        <div className="plan-details">
            <div className="plan-details__summary">
                <div className="plan-details__field">
                    <span>{t('plan')}</span>
                    <strong>{plan?.label || '-'}</strong>
                </div>
                <div className="plan-details__field">
                    <span>{t('price')}</span>
                    <strong>
                        {price != null ? `${t('currency_symbol')} ${convertDoubleToStringPTBR(price)}` : '-'}
                    </strong>
                </div>
                <div className="plan-details__field">
                    <span>{t('periodicity')}</span>
                    <strong>{periodicityLabel}</strong>
                </div>

                {planStatus && (
                    <div className="plan-details__field plan-details__status">
                        <span>Status</span>
                        {planStatus.hasOverdueCharge ? (
                            <Badge bg="warning" text="dark" className="plan-details__status-badge plan-details__status-badge--warning">
                                <i className="fa fa-triangle-exclamation me-1"></i>
                                {planStatus.overdueChargeCount > 1
                                    ? `${planStatus.overdueChargeCount} cobranças em atraso`
                                    : 'Cobrança em atraso'}
                            </Badge>
                        ) : (
                            <Badge bg="success" className="plan-details__status-badge plan-details__status-badge--success">
                                <i className="fa fa-check-circle me-1"></i>
                                Em dia
                            </Badge>
                        )}
                    </div>
                )}

                {hasPlan && showDetailsButton && (
                    <button
                        type="button"
                        className="plan-details__toggle"
                        onClick={() => setExpanded((current) => !current)}
                        aria-expanded={expanded}
                        aria-controls="plan-details-panel"
                    >
                        <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-circle-info'}`} aria-hidden="true"></i>
                        {expanded ? t('plan_details_hide') : t('plan_details_show')}
                    </button>
                )}
            </div>

            {hasPlan && expanded && (
                <div className="plan-details__panel" id="plan-details-panel">
                    <div className="plan-details__metrics">
                        <div className="plan-details__metric">
                            <span>{t('plan_usage_limit')}</span>
                            <strong>{maxAppointmentsLabel}</strong>
                            <small>{t('plan_usage_limit_hint')}</small>
                        </div>
                        <div className="plan-details__metric">
                            <span>{t('qty_transactions')}</span>
                            <strong>{qtyTransactionsLabel}</strong>
                            <small>{t('plan_qty_transactions_hint')}</small>
                        </div>
                    </div>

                    <div className="plan-details__products">
                        <span className="plan-details__products-title">{t('plan_included_products')}</span>

                        {products.length > 0 ? (
                            <ul className="plan-details__product-list">
                                {products.map((product) => (
                                    <li key={product.id ?? product.productId}>
                                        <i className="fa-solid fa-check" aria-hidden="true"></i>
                                        <span>{product.productName}</span>
                                        {/* Desconto só aparece quando existe: produto incluso sem
                                            desconto é a maioria dos casos e não precisa de selo. */}
                                        {product.discountPercentage > 0 && (
                                            <em className="plan-details__product-discount">
                                                {`${convertDoubleToStringPTBR(product.discountPercentage)}% ${t('discount')}`}
                                            </em>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="plan-details__empty">{t('plan_no_products')}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanDetails;
